import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getFileExtension, getFileName, getFileType, getFormatedFileName } from 'utils/FileUtils'
import useSandreList from 'utils/customHook/useSandreList'
import moment from 'moment'
import ToastrAction from 'toastr/actions/ToastrAction'
import i18n from 'simple-react-i18n'
import { getStationTypeCodeFromType } from 'utils/StationUtils'
import { PICTURE_TYPES } from 'components/file/constant/FileConstants'
import StationAction from 'station/actions/StationAction'
import { getSiteUrl } from 'utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION } from 'station/constants/StationConstants'
import { isNil } from 'lodash'
import { getDate } from 'utils/DateUtil'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Checkbox from 'components/forms/Checkbox'
import { CardTable } from 'components/datatable/NewTable'
import { nbPerPageLabelTiny } from 'referencial/constants/ReferencialConstants'
import DtoFile from 'components/file/dto/DtoFile'
import DtoQualitometer from 'quality/dto/DtoQualitometer'
import DtoPiezometer from 'piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import ResourceDto from 'resource/dto/ResourceDto'
import { hasValue } from 'utils/NumberUtil'
import { ShapeSamplePoint } from 'station/shapes/StationShapes'

const ImportDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    files = [],
    station,
    stationType,
    linkSamplePoints,
}) => {
    const [name, setName] = useState('')
    const [content, setContent] = useState('')
    const [fileType, setFileType] = useState(0)
    const [date, setDate] = useState('')
    const [samplePoint, setSamplePoint] = useState({})
    const [active, setActive] = useState(false)
    const [shared, setShared] = useState(false)

    const {
        associatedSites,
    } = useSelector(store => ({
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (isOpen) return

        setName('')
        setContent('')
        setFileType(0)
        setDate('')
        setActive(false)
        setShared(false)
    }, [isOpen])

    const onImportFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        setName(file.name)
        reader.onload = upload => setContent(upload.target.result)
        reader.readAsDataURL(file)
    }

    const { sandreCode, title } = getFileType(content)
    const fileTypes = useSandreList(sandreCode)

    const saveFile = () => {
        const now = moment().valueOf()
        const activeInt = active ? 1 : 0
        const ft = fileTypes.find(t => t.code === fileType)
        const point = linkSamplePoints?.find(sp => sp.idPoint === samplePoint)
        if (!isNil(point) && !hasValue(point.identifiant)) {
            dispatch(ToastrAction.warning(i18n.samplingPointWithoutIdCantBeAffected))
            return
        }
        const formattedName = getFormatedFileName(
            station.code,
            activeInt,
            getFileName(name),
            getFileExtension(name),
            ft.mnemonique || ft.name,
            hasValue(date) ? date : now,
            stationType,
            shared,
            point,
        )
        if (files.some(f => f.name === formattedName)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return
        }

        const formattedFile = {
            stationType: getStationTypeCodeFromType(stationType),
            stationId: station.id,
            path: '',
            content,
            fileType,
            active: activeInt,
            date: hasValue(date) ? date : now,
            name: formattedName,
        }

        const type = content.includes(PICTURE_TYPES.IMAGE) ? 'picture' : 'doc'

        dispatch(StationAction.addStationFile(formattedFile, type)).then(() => {
            onValidate()
            onClose()
        })
    }

    const formattedAssociatedSites = useMemo(() => {
        return associatedSites.filter(as => as.code === as.stationLinkedCode).map(d => ({
            stationType: (
                <img
                    src={getSiteUrl(STATION_NAME_ASSOCIATION[d.typeName])}
                    style={{ maxHeight: '30px' }}
                />
            ),
            code: d.code,
            name: d.stationLinkedName,
        }))
    }, [associatedSites])

    const samplePoints = useMemo(() => {
        if (stationType !== 'quality') return []
        return linkSamplePoints
            ?.filter(sp => !isNil(sp.identifiant) || !isNil(sp.name))
            .map(sp => ({
                id: sp.idPoint,
                label: `${isNil(sp.identifiant) ? '' : `[${sp.identifiant}] `}${sp.name}${isNil(sp.startDate) ? '' : ` (${getDate(sp.startDate)})`}`,
            }))
    }, [station, stationType])

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth='lg'
        >
            <DefaultDialogTitle
                title={i18n.importFile}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container rowSpacing={0.5} columnSpacing={1} alignItems='center'>
                            <Grid size='auto'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    component='label'
                                    fullWidth
                                >
                                    <span>{i18n.chooseFile}</span>
                                    <input
                                        type='file'
                                        hidden
                                        onChange={onImportFile}
                                    />
                                </Button>
                            </Grid>
                            <Grid size='grow'>
                                <Input title={i18n.fileName} value={hasValue(name) ? name : i18n.pleaseSelectFile} disabled/>
                            </Grid>
                            {hasValue(name) && (
                                <>
                                    <Grid size={12}>
                                        <Select
                                            label={title}
                                            options={fileTypes}
                                            value={fileType}
                                            onChange={setFileType}
                                            noNullValue
                                            noSort
                                            keyLabel='name'
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <SimpleDatePicker
                                            label={i18n.date}
                                            value={date}
                                            onChange={setDate}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <Checkbox
                                            onChange={setActive}
                                            checked={active}
                                            label={i18n.active}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        {stationType === 'quality' && (
                                            <Select
                                                label={i18n.samplePoint}
                                                options={samplePoints}
                                                value={samplePoint}
                                                onChange={setSamplePoint}
                                                noSort
                                            />
                                        )}
                                    </Grid>
                                    <Grid size={8}>
                                        <CardTable
                                            title={i18n.sharedWithOtherStations}
                                            rows={formattedAssociatedSites}
                                            headers={['stationType', 'code', 'name']}
                                            rowsPerPageOptions={nbPerPageLabelTiny}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <Checkbox
                                            onChange={setShared}
                                            checked={shared}
                                            label={i18n.sharedWithOtherStations}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={saveFile}
                    variant='contained'
                    color='primary'
                    disabled={isNil(content) || isNil(name) || isNil(fileType)}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ImportDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    station: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(PluviometerDto),
        PropTypes.instanceOf(DtoInstallation),
        PropTypes.instanceOf(DtoDistributionUnit),
        ProductionUnitShape,
        PropTypes.instanceOf(ResourceDto),
    ]),
    stationType: PropTypes.string,
    loadFiles: PropTypes.func,
    linkSamplePoints: PropTypes.arrayOf(ShapeSamplePoint),
}

export default ImportDialog